// client/src/components/ticketing/TicketConfig.jsx
//
// Tenant-admin ticketing page for one event item: enable/disable ticketing,
// manage ticket tiers, and (via the two sub-panels) custom registration
// fields and promo codes. Analog of RaffleConfig.jsx, but talking to the new
// parallel ticketing tables/routes (server/routes/ticketingRoutes.js)
// instead of t_raffle. Rendered as an inline modal from EventItemDetail.jsx,
// same as the other action-type configs — shares AuctionConfig.css's fixed
// dark-blue theme (not Tailwind dark: classes) so it renders correctly
// regardless of OS color-scheme.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../event/AuctionConfig.css";
import "./TicketConfig.css";
import RegistrationFieldBuilder from "./RegistrationFieldBuilder";
import PromoCodeManager from "./PromoCodeManager";
import SendTicketsPanel from "./SendTicketsPanel";
import PricePhaseTimeline from "./PricePhaseTimeline";
import WaitlistPanel from "./WaitlistPanel";
import { useTranslation } from "react-i18next";
import { getCurrencyOptions, DEFAULT_CURRENCY } from "../../utils/currencyUtils";

const currencyOptions = getCurrencyOptions();
const baseApiUrl = process.env.REACT_APP_BASE_URL || "";
const appOrigin = (process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "") || "").replace(/\/+$/, "");

const EMPTY_TIER = {
  name: "",
  tier_type: "",
  price: "0",
  currency: DEFAULT_CURRENCY,
  capacity: "",
  min_per_order: "1",
  max_per_order: "10",
  visibility: "public",
  description: "",
};

const TABS = [
  { key: "tiers", label: "Ticket Tiers" },
  { key: "fields", label: "Registration Fields" },
  { key: "promo", label: "Promo Codes" },
  { key: "send", label: "Send Tickets" },
  { key: "waitlist", label: "Waitlist", labelKey: "ticketing.waitlist.tab" },
];

export default function TicketConfig({ tenantId, eventId, itemId, itemName, onClose }) {
  const { t } = useTranslation();
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;
  const [openPhasesFor, setOpenPhasesFor] = useState(null);

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [activeTab, setActiveTab] = useState("tiers");
  const [message, setMessage] = useState({ text: "", type: "" });

  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState(EMPTY_TIER);
  const [editingTierId, setEditingTierId] = useState(null);
  const [savingTier, setSavingTier] = useState(false);
  const [tenantSlug, setTenantSlug] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${baseApiUrl}/api/tenants/${tenantId}`)
      .then(({ data }) => { if (!cancelled) setTenantSlug(data?.tenant?.slug || data?.slug || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenantId]);

  const purchaseUrl = tenantSlug ? `${appOrigin}/e/${tenantSlug}/${eventId}/${itemId}` : null;

  const copyPurchaseLink = async () => {
    if (!purchaseUrl) return;
    try {
      await navigator.clipboard.writeText(purchaseUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${base}/config`);
      setEnabled(!!data.success);
    } catch (err) {
      if (err?.response?.status === 404) {
        setEnabled(false);
      } else {
        setMessage({ text: "Failed to load ticketing configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  }, [base]);

  const loadTiers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/tiers`);
      if (data.success) setTiers(data.tiers);
    } catch (err) {
      console.error("Error loading ticket tiers:", err);
    }
  }, [base]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (enabled) loadTiers();
  }, [enabled, loadTiers]);

  const handleEnable = async () => {
    setEnabling(true);
    setMessage({ text: "", type: "" });
    try {
      const { data } = await axios.post(`${base}/config`, {});
      if (data.success) {
        setEnabled(true);
        setMessage({ text: data.message || "Ticketing enabled", type: "success" });
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to enable ticketing", type: "error" });
    } finally {
      setEnabling(false);
    }
  };

  const resetTierForm = () => {
    setTierForm(EMPTY_TIER);
    setEditingTierId(null);
  };

  const startEditTier = (tier) => {
    setEditingTierId(tier.id);
    setTierForm({
      name: tier.name || "",
      tier_type: tier.tier_type || "",
      price: String(tier.price ?? "0"),
      currency: tier.currency ? String(tier.currency).toUpperCase() : DEFAULT_CURRENCY,
      capacity: tier.capacity != null ? String(tier.capacity) : "",
      min_per_order: String(tier.min_per_order ?? "1"),
      max_per_order: String(tier.max_per_order ?? "10"),
      visibility: tier.visibility || "public",
      description: tier.description || "",
    });
  };

  const submitTier = async (e) => {
    e.preventDefault();
    setSavingTier(true);
    setMessage({ text: "", type: "" });

    const payload = {
      name: tierForm.name.trim(),
      tier_type: tierForm.tier_type.trim() || null,
      price: Number(tierForm.price),
      currency: tierForm.currency || DEFAULT_CURRENCY,
      capacity: tierForm.capacity === "" ? null : Number(tierForm.capacity),
      min_per_order: Number(tierForm.min_per_order) || 1,
      max_per_order: Number(tierForm.max_per_order) || 10,
      visibility: tierForm.visibility || "public",
      description: tierForm.description.trim() || null,
    };

    try {
      const { data } = editingTierId
        ? await axios.put(`${base}/tiers/${editingTierId}`, payload)
        : await axios.post(`${base}/tiers`, payload);

      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        resetTierForm();
        loadTiers();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to save tier", type: "error" });
    } finally {
      setSavingTier(false);
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm("Delete this ticket tier? This cannot be undone.")) return;
    try {
      await axios.delete(`${base}/tiers/${tierId}`);
      loadTiers();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete tier", type: "error" });
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading ticketing configuration...</div>
      </div>
    );
  }

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>Ticketing{itemName ? ` — ${itemName}` : ""}</h2>
        {onClose && (
          <button onClick={onClose} className="close-button">
            ×
          </button>
        )}
      </div>

      {enabled && purchaseUrl && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13 }}>
          <span style={{ opacity: 0.75 }}>Ticket page:</span>
          <code style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{purchaseUrl}</code>
          <button type="button" onClick={copyPurchaseLink} className="ticket-item-btn">
            {linkCopied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {!enabled ? (
        <div className="share-link-section" style={{ textAlign: "center" }}>
          <h3>🎟️ Ticketing isn't enabled yet</h3>
          <p className="share-link-description">
            Enable ticketing for this event item to configure ticket tiers, custom registration
            fields, and promo codes.
          </p>
          <button onClick={handleEnable} disabled={enabling} className="save-button" style={{ flex: "none", padding: "12px 28px" }}>
            {enabling ? "Enabling…" : "Enable Ticketing"}
          </button>
        </div>
      ) : (
        <>
          <div className="ticket-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`ticket-tab ${activeTab === tab.key ? "active" : ""}`}
              >
                {tab.labelKey ? t(tab.labelKey, tab.label) : tab.label}
              </button>
            ))}
          </div>

          {activeTab === "tiers" && (
            <div>
              <form onSubmit={submitTier} className="auction-config-form" style={{ padding: 16, borderRadius: 12, marginBottom: 16 }}>
                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="tier_name">Tier Name <span className="required">*</span></label>
                    <input
                      id="tier_name"
                      value={tierForm.name}
                      onChange={(e) => setTierForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Early Bird, VIP, Group..."
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier_price">Price <span className="required">*</span></label>
                    <input
                      id="tier_price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={tierForm.price}
                      onChange={(e) => setTierForm((f) => ({ ...f, price: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tier_currency">Currency</label>
                    <select
                      id="tier_currency"
                      value={tierForm.currency}
                      onChange={(e) => setTierForm((f) => ({ ...f, currency: e.target.value }))}
                    >
                      {currencyOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="tier_description">{t("ticketing.tier.description", "Description (shown to buyers)")}</label>
                    <textarea
                      id="tier_description"
                      rows={2}
                      value={tierForm.description}
                      onChange={(e) => setTierForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder={t("ticketing.tier.descriptionPlaceholder", "What's included")}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier_visibility">{t("ticketing.tier.visibility", "Who can see it")}</label>
                    <select
                      id="tier_visibility"
                      value={tierForm.visibility}
                      onChange={(e) => setTierForm((f) => ({ ...f, visibility: e.target.value }))}
                    >
                      <option value="public">{t("ticketing.tier.visibilityPublic", "Everyone (listed)")}</option>
                      <option value="code_only">{t("ticketing.tier.visibilityCode", "Only with an access code")}</option>
                      <option value="hidden">{t("ticketing.tier.visibilityHidden", "Hidden (staff send only)")}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier_capacity">Capacity</label>
                    <input
                      id="tier_capacity"
                      type="number"
                      min="0"
                      value={tierForm.capacity}
                      onChange={(e) => setTierForm((f) => ({ ...f, capacity: e.target.value }))}
                      placeholder="Blank = unlimited"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="tier_min">Min per order</label>
                    <input
                      id="tier_min"
                      type="number"
                      min="1"
                      value={tierForm.min_per_order}
                      onChange={(e) => setTierForm((f) => ({ ...f, min_per_order: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tier_max">Max per order</label>
                    <input
                      id="tier_max"
                      type="number"
                      min="1"
                      value={tierForm.max_per_order}
                      onChange={(e) => setTierForm((f) => ({ ...f, max_per_order: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-actions" style={{ marginTop: 12, paddingTop: 12 }}>
                  <button type="submit" disabled={savingTier} className="save-button">
                    {savingTier ? "Saving…" : editingTierId ? "Update Tier" : "Add Tier"}
                  </button>
                  {editingTierId && (
                    <button type="button" onClick={resetTierForm} className="delete-button" style={{ background: "#5a5a55" }}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {tiers.length === 0 ? (
                <div className="ticket-empty-state">Add your first ticket tier above: buyers choose from the tiers listed here.</div>
              ) : (
                <ul className="ticket-list">
                  {tiers.map((tier) => (
                    <React.Fragment key={tier.id}>
                      <li className="ticket-list-item">
                        <div>
                          <div className="ticket-list-item-title">
                            {tier.name}
                            {tier.visibility === "code_only" && (
                              <span className="ticket-badge">{t("ticketing.tier.badgeCode", "Access code")}</span>
                            )}
                            {tier.visibility === "hidden" && (
                              <span className="ticket-badge">{t("ticketing.tier.badgeHidden", "Hidden")}</span>
                            )}
                          </div>
                          <div className="ticket-list-item-meta">
                            {Number(tier.price).toFixed(2)} {String(tier.currency).toUpperCase()} ·{" "}
                            {tier.capacity == null ? "Unlimited" : `${tier.quantity_sold}/${tier.capacity} sold`}
                          </div>
                          {tier.description && <div className="ticket-list-item-meta">{tier.description}</div>}
                        </div>
                        <div className="ticket-list-item-actions">
                          <button
                            onClick={() => setOpenPhasesFor((id) => (id === tier.id ? null : tier.id))}
                            className="ticket-item-btn"
                            aria-expanded={openPhasesFor === tier.id}
                          >
                            {t("ticketing.tier.phasesToggle", "Price phases")}
                          </button>
                          <button onClick={() => startEditTier(tier)} className="ticket-item-btn">
                            Edit
                          </button>
                          <button onClick={() => deleteTier(tier.id)} className="ticket-item-btn danger">
                            Delete
                          </button>
                        </div>
                      </li>
                      {openPhasesFor === tier.id && (
                        <li style={{ listStyle: "none" }}>
                          <PricePhaseTimeline tenantId={tenantId} eventId={eventId} itemId={itemId} tier={tier} />
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === "fields" && (
            <RegistrationFieldBuilder tenantId={tenantId} eventId={eventId} itemId={itemId} />
          )}

          {activeTab === "promo" && (
            <PromoCodeManager tenantId={tenantId} eventId={eventId} itemId={itemId} tiers={tiers} />
          )}

          {activeTab === "send" && (
            <SendTicketsPanel tenantId={tenantId} eventId={eventId} itemId={itemId} tiers={tiers} />
          )}

          {activeTab === "waitlist" && (
            <WaitlistPanel tenantId={tenantId} eventId={eventId} itemId={itemId} />
          )}
        </>
      )}
    </div>
  );
}
